import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useTranslation } from "@refinedev/core";

import { useMessengerEnabled } from "@/hooks/useMessengerEnabled";
import { useMessengerLogin, usePatientChatRoom } from "@/hooks/usePatientMessenger";
import { ChatConfiguration, ChatGraphQLError } from "@/utils/GraphQLClient/messenger";
import { toMessengerLocale } from "@/utils/i18n-locale";

import type { IConfig } from "@ethora/chat-component/dist/types/types";

interface MessengerContextValue {
  /**
   * Whether the org has the messenger feature (env flag AND the practitioner's
   * `messenger:UseChat` permission). Every messenger surface hides while this is false.
   */
  enabled: boolean;
  /**
   * True while the licence check behind `enabled` is still in flight. `enabled` is false
   * meanwhile (fail closed), so surfaces that would otherwise pop in once the permission
   * resolves should render nothing until this is false.
   */
  enabledLoading: boolean;
  fhirPatientId: string | null;
  isLoading: boolean;
  token: string | null;
  chatRoomJid: string | null;
  config: ChatConfiguration | null;
  error?: ChatGraphQLError;
  setFhirPatientId: (id: string | null) => void;
  setChatRoomJid: (patientId: string) => void;
  removeChatRoomJid: () => void;
  xmppProviderFlags: IConfig;
}

const MessengerContext = createContext<MessengerContextValue>({
  fhirPatientId: null,
  isLoading: true,
  token: null,
  chatRoomJid: null,
  config: null,
  error: undefined,
  setFhirPatientId: () => {},
  setChatRoomJid: () => {},
  removeChatRoomJid: () => {},
  xmppProviderFlags: {},
  // Fail closed outside the provider: no messenger surface shows without a licence check
  enabled: false,
  enabledLoading: false,
});

export const MessengerProvider = ({ children }: { children: React.ReactNode }) => {
  const { getLocale } = useTranslation();
  const readerLocale = toMessengerLocale(getLocale());
  const { enabled, loading: enabledLoading } = useMessengerEnabled();
  const { token, config, loading, error: loginError } = useMessengerLogin(enabled);
  const [fhirPatientId, setFhirPatientId] = useState<string | null>(null);
  const [chatRoomJid, setChatRoomJidState] = useState<string | null>(null);

  const {
    room_jid,
    error: chatRoomError,
    loading: chatRoomLoading,
    reexecute_query,
  } = usePatientChatRoom(fhirPatientId ?? undefined, enabled);

  useEffect(() => {
    if (fhirPatientId) {
      setChatRoomJidState(room_jid ?? null);
    } else {
      setChatRoomJidState(null);
    }
  }, [fhirPatientId, room_jid]);

  const setChatRoomJid = useCallback(() => {
    reexecute_query?.();
  }, [reexecute_query]);

  const removeChatRoomJid = useCallback(() => {
    setChatRoomJidState(null);
  }, []);

  const xmppProviderFlags: IConfig = useMemo(
    () => ({
      // UI flags — must match Chat's config so the store always has them
      disableInteractions: true,
      setRoomJidInPath: false,
      disableNewChatButton: true,
      disableRoomConfig: true,
      disableProfilesInteractions: true,
      disableChatInfo: {
        disableHeader: false,
        disableDescription: true,
        disableType: true,
        disableMembers: true,
        disableChatHeaderMenu: true,
      },
      chatHeaderSettings: {
        hide: false,
        disableCreate: true,
        disableMenu: true,
      },
      // Mirrors base-messenger's Chat config so the store carries the same translation policy.
      // readerLocale comes from the dashboard language; changing it rebuilds this config and
      // resets the XMPP connection, which is acceptable since a language switch already leaves chat.
      translates: {
        enabled: true,
        mode: "manual",
        forceType: true,
        readerLocale,
        showLanguageSelector: false,
      },
      i18n: { locale: readerLocale },
    }),
    [readerLocale]
  );

  const value = useMemo(
    () => ({
      fhirPatientId,
      isLoading: loading || chatRoomLoading || (!chatRoomJid && !(loginError || chatRoomError)),
      token: token ?? null,
      config: config ?? null,
      chatRoomJid,
      error: loginError || chatRoomError,
      setFhirPatientId,
      setChatRoomJid,
      removeChatRoomJid,
      xmppProviderFlags,
      enabled,
      enabledLoading,
    }),
    [
      chatRoomError,
      chatRoomJid,
      chatRoomLoading,
      config,
      enabled,
      enabledLoading,
      fhirPatientId,
      loading,
      loginError,
      removeChatRoomJid,
      setChatRoomJid,
      token,
      xmppProviderFlags,
    ]
  );

  return <MessengerContext.Provider value={value}>{children}</MessengerContext.Provider>;
};

export const useMessengerContext = (): MessengerContextValue => useContext(MessengerContext);
